import type { EintragVerortung, PlanCoordinates, ProjektVerortung, VerortungPlan } from '../../types';
import { istImPlan, kompassZuPlanwinkel, meterZuPlanProzent, utmZuPlan } from './georeferenz';
import { wgs84ZuUtm } from './utm';
import { planGeoreferenz } from './projektVerortung';
import type { SensorSchnappschuss } from './ortsvorschlag';
import { geschossEbenen, type Ebene, type EbenenSchaetzung } from './ebenen';

/**
 * Live-Standort auf einem Plan (Betreiber 27.09.: „beim Verorten blinkt ein
 * Punkt mit Richtung als Live-Standort, den man korrigieren kann“).
 *
 * Reine Rechnung: aus dem aktuellen Sensor-Schnappschuss und der Kalibrierung
 * EINES Plans → Punkt (Plan-Prozent), Unsicherheitskreis und Blickrichtung.
 * Der Punkt ist der Standort des Geräts; der Kegel zeigt, wohin es schaut.
 */

/**
 * Live großzügiger als der Pin-Vorschlag der Kamera (8 m): der Punkt ist nur
 * Orientierung und zeigt seine Unsicherheit als Kreis. Schlechter als 15 m
 * (unter Decken, in Stahlbeton) wird er nicht gezeigt — dort hilft er nicht.
 */
export const LIVE_MAX_GENAUIGKEIT_M = 15;
/** Knapp neben dem Planbild (Rand in Prozent) wird der Punkt an den Rand gesetzt statt verworfen. */
export const LIVE_RAND_PROZENT = 5;

export interface LivePunkt {
    pin: PlanCoordinates;
    radius: { rx: number; ry: number };
    genauigkeitM: number;
    /** Blickrichtung im Plan (Grad im Uhrzeigersinn ab Plan-oben), nur bei Kompass-Genauigkeit ≥ 2. */
    blickGradPlan: number | null;
}

const klemmen = (w: number) => Math.min(100, Math.max(0, w));

/** Punkt dieses Plans; null ohne Georeferenz, ohne ausreichend genaues GPS oder außerhalb des Plans. */
export function livePunktFuerPlan(
    plan: VerortungPlan | undefined,
    s: SensorSchnappschuss,
    maxGenauigkeitM: number = LIVE_MAX_GENAUIGKEIT_M,
): LivePunkt | null {
    const pos = s.position;
    if (!pos || !Number.isFinite(pos.genauigkeitM) || pos.genauigkeitM > maxGenauigkeitM) return null;
    const geo = planGeoreferenz(plan);
    if (!geo || 'fehler' in geo) return null;
    const utm = wgs84ZuUtm(pos.lat, pos.lng, geo.zone);
    const p = utmZuPlan(geo, utm.ost, utm.nord);
    if (!istImPlan(p, LIVE_RAND_PROZENT)) return null;
    const r = s.richtung;
    const blickGradPlan = r && r.genauigkeit >= 2
        ? kompassZuPlanwinkel(geo, r.azimutGrad, r.deklinationGrad, pos.lat, pos.lng)
        : null;
    return {
        pin: { x: klemmen(p.x), y: klemmen(p.y) },
        radius: meterZuPlanProzent(geo, pos.genauigkeitM),
        genauigkeitM: pos.genauigkeitM,
        blickGradPlan: blickGradPlan !== null && Number.isFinite(blickGradPlan) ? blickGradPlan : null,
    };
}

/** Geschoss, das ein Plan zeigt (Geschossplan mit Zuordnung) — sonst null (Lageplan, nicht zugeordnet). */
export function ebeneFuerPlan(v: ProjektVerortung, planId: string): Ebene | null {
    const vp = v.plaene?.[planId];
    if (!vp || vp.art === 'lageplan' || !vp.geschossId) return null;
    return geschossEbenen(v.geschosse ?? []).find((e) => e.id === vp.geschossId) ?? null;
}

/**
 * Ebene für die Übernahme des Live-Punkts auf `planId`: zeigt der Plan ein
 * Geschoss, gilt DIESES (der Nutzer hat den Plan gewählt — Codex-Review 27.09.:
 * sonst Pin im 2. OG, Ortstext „1. OG“); sonst die Schätzung, außer „zwischen“.
 */
export function liveEbene(v: ProjektVerortung, planId: string, schaetzung: EbenenSchaetzung | null): Ebene | null {
    return ebeneFuerPlan(v, planId) ?? (schaetzung && !schaetzung.zwischen ? schaetzung.ebene : null);
}

/**
 * Ergebnis am Eintrag, wenn der Live-Standort als Pin übernommen wird.
 * `ebene` = bestätigte Ebene (siehe `liveEbene`); weicht sie von der Schätzung
 * ab, ist die Sicherheit 1 (vom Nutzer bestätigt).
 */
export function liveEintragVerortung(punkt: LivePunkt, schaetzung: EbenenSchaetzung | null, ebene?: Ebene | null): EintragVerortung {
    const geschaetzt = schaetzung && !schaetzung.zwischen ? schaetzung.ebene : null;
    const gewaehlt = ebene === undefined ? geschaetzt : ebene;
    const bestaetigtAbweichend = !!gewaehlt && gewaehlt.id !== geschaetzt?.id;
    return {
        quelle: 'sensor',
        ebene: gewaehlt?.name ?? null,
        hoeheM: bestaetigtAbweichend ? gewaehlt.hoeheM : schaetzung ? schaetzung.hoeheM : null,
        genauigkeitM: punkt.genauigkeitM,
        blickGradPlan: punkt.blickGradPlan,
        sicherheit: bestaetigtAbweichend ? 1 : schaetzung ? schaetzung.sicherheit : null,
    };
}
